/**
 * Simulated GET /api/qb-budget — Company → Financials → "Budget vs Actual".
 *
 * Shape is `BudgetStatus`/`BudgetPayload` from src/lib/api/qbFinancials.ts plus
 * the extra snapshot columns Budget.tsx reads directly (`id`, `fiscal_year`,
 * `budget_doc`).
 *
 * GOALS ARE REAL. Every `budget` number is summed straight out of
 * public/data/budget-2026.json — the same file the production endpoint reads —
 * income being the accounts whose number starts with 4.
 *
 * ACTUALS follow finance.ts: income is real job_invoices revenue mapped onto
 * those accounts by division; COGS/expenses are generated from each account's
 * budgeted share. `?method=cash` runs the same build through the collected /
 * invoiced ratio taken from v_jobs_full; `?method=accrual` (the default) does
 * not.
 */
import { once } from './prng';
import {
  budgetDoc, budgetTotalsFor, monthRange, nyDate, pnlAccountsFor, round2,
  type PnlAccounts, type PnlLine,
} from './finance';

export type BudgetMethod = 'accrual' | 'cash';

interface Line { actual: number; budget: number; variance: number; variancePct: number }
interface MonthlyCell extends Line { month: string }
interface Node extends Line {
  name: string;
  type: 'section' | 'summary' | 'leaf';
  group?: string | null;
  monthly?: MonthlyCell[];
  children: Node[];
}

function line(actual: number, budget: number): Line {
  const variance = round2(actual - budget);
  return {
    actual: round2(actual),
    budget: round2(budget),
    variance,
    variancePct: budget !== 0 ? round2((variance / Math.abs(budget)) * 100) : 0,
  };
}

function sectionOf(
  name: string,
  group: string,
  lines: PnlLine[],
  monthlyByAccount: Map<string, MonthlyCell[]>,
): Node {
  const children: Node[] = lines.map((l) => ({
    name: l.name, type: 'leaf', group,
    ...line(l.actual, l.budget),
    monthly: monthlyByAccount.get(l.name) ?? [],
    children: [],
  }));
  const actual = children.reduce((s, c) => s + c.actual, 0);
  const budget = children.reduce((s, c) => s + c.budget, 0);
  return { name, type: 'section', group, ...line(actual, budget), children };
}

async function build(method: BudgetMethod) {
  const doc = await budgetDoc();
  const cash = method === 'cash';
  const today = nyDate();
  const fyStartMonth = doc.fyStart.slice(0, 7);
  const fyEndMonth = doc.fyEnd.slice(0, 7);
  const fyMonths = monthRange(fyStartMonth, fyEndMonth);
  const currentMonth = today.slice(0, 7);
  const ytdMonths = fyMonths.filter((m) => m <= currentMonth);
  const months = ytdMonths.length ? ytdMonths : [fyMonths[0]];

  // One P&L per month so every leaf can carry its monthly cells and the YTD
  // roll-up is the exact sum of them.
  const perMonth = await Promise.all(
    months.map(async (m) => ({ month: m, accounts: await pnlAccountsFor([m], `budget-${m}`, { cash }) })),
  );

  const monthlyByAccount = new Map<string, MonthlyCell[]>();
  const totalsByAccount = new Map<string, { line: PnlLine; group: 'Income' | 'COGS' | 'Expenses' }>();
  for (const { month, accounts } of perMonth) {
    const groups: [keyof Pick<PnlAccounts, 'income' | 'cogs' | 'expenses'>, 'Income' | 'COGS' | 'Expenses'][] = [
      ['income', 'Income'], ['cogs', 'COGS'], ['expenses', 'Expenses'],
    ];
    for (const [key, group] of groups) {
      for (const l of accounts[key]) {
        const cells = monthlyByAccount.get(l.name) ?? [];
        cells.push({ month, ...line(l.actual, l.budget) });
        monthlyByAccount.set(l.name, cells);
        const agg = totalsByAccount.get(l.name);
        if (agg) {
          agg.line.actual = round2(agg.line.actual + l.actual);
          agg.line.budget = round2(agg.line.budget + l.budget);
        } else {
          totalsByAccount.set(l.name, { line: { ...l }, group });
        }
      }
    }
  }

  const pick = (g: 'Income' | 'COGS' | 'Expenses') =>
    [...totalsByAccount.values()].filter((v) => v.group === g).map((v) => v.line)
      .sort((a, b) => a.number.localeCompare(b.number));

  const incomeSection = sectionOf('Income', 'Income', pick('Income'), monthlyByAccount);
  const cogsSection = sectionOf('Cost of Goods Sold', 'COGS', pick('COGS'), monthlyByAccount);
  const expenseSection = sectionOf('Expenses', 'Expenses', pick('Expenses'), monthlyByAccount);

  const grossProfit = line(incomeSection.actual - cogsSection.actual, incomeSection.budget - cogsSection.budget);
  const netIncome = line(grossProfit.actual - expenseSection.actual, grossProfit.budget - expenseSection.budget);

  const sections: Node[] = [
    incomeSection,
    cogsSection,
    { name: 'Gross Profit', type: 'summary', group: 'Income', ...grossProfit, children: [] },
    expenseSection,
    { name: 'Net Income', type: 'summary', group: 'Income', ...netIncome, children: [] },
  ];

  // Quarters, on revenue.
  const revenueByMonth = new Map<string, Line>();
  for (const cell of incomeSection.children.flatMap((c) => c.monthly ?? [])) {
    const cur = revenueByMonth.get(cell.month) ?? { actual: 0, budget: 0, variance: 0, variancePct: 0 };
    revenueByMonth.set(cell.month, line(cur.actual + cell.actual, cur.budget + cell.budget));
  }
  // A quarter still in the future has no actual but keeps its full budgeted goal.
  const quarterBudgets = await Promise.all(
    [0, 1, 2, 3].map((q) => budgetTotalsFor(fyMonths.slice(q * 3, q * 3 + 3))),
  );
  const quarters = [0, 1, 2, 3].map((q) => {
    const actual = fyMonths.slice(q * 3, q * 3 + 3)
      .reduce((s, m) => s + (revenueByMonth.get(m)?.actual ?? 0), 0);
    return line(actual, quarterBudgets[q].income);
  });

  const fullYear = await budgetTotalsFor(fyMonths);

  const day = Number(today.slice(8, 10));
  const daysInMonth = new Date(Number(today.slice(0, 4)), Number(today.slice(5, 7)), 0).getDate();
  const monthsElapsed = round2(months.length - 1 + day / daysInMonth);

  return {
    asOf: new Date().toISOString(),
    method,
    budget: {
      id: `demo-${doc.fyStart.slice(0, 4)}`,
      name: doc.budgetName,
      startDate: doc.fyStart,
      endDate: doc.fyEnd,
    },
    period: {
      fyStart: doc.fyStart, fyEnd: doc.fyEnd, ytdEnd: today,
      monthsThrough: currentMonth, monthsElapsed,
    },
    months: fyMonths,
    ytd: {
      totals: {
        revenue: line(incomeSection.actual, incomeSection.budget),
        cogs: line(cogsSection.actual, cogsSection.budget),
        expenses: line(expenseSection.actual, expenseSection.budget),
        netIncome,
        grossProfit,
      },
      sections,
    },
    quarters,
    fullYear: { totals: { revenue: line(incomeSection.actual, fullYear.income) } },
    simulated: true,
  };
}

const accrual = once(() => build('accrual'));
const cashBasis = once(() => build('cash'));

export async function simulatedBudget(method: BudgetMethod = 'accrual') {
  const payload = await (method === 'cash' ? cashBasis() : accrual());
  const doc = await budgetDoc();
  return {
    ok: true,
    qb_configured: true,
    simulated: true,
    method,
    demo_note:
      `Budget goals are the real public/data/budget-2026.json (income = accounts starting 4), summed per month; `
      + `actuals are derived monthly revenue from job_invoices with generated cost lines. method=${method}`
      + `${method === 'cash' ? ' applies the collected/invoiced ratio from v_jobs_full.' : ' is invoice-dated (accrual).'}`,
    snapshot: {
      id: `demo-budget-${doc.fyStart.slice(0, 4)}-${method}`,
      period_start: payload.period.fyStart,
      period_end: payload.period.ytdEnd,
      fiscal_year: Number(doc.fyStart.slice(0, 4)),
      payload,
      budget_doc: { name: doc.budgetName, fyStart: doc.fyStart, fyEnd: doc.fyEnd },
      source: 'demo-simulated',
      updated_at: payload.asOf,
    },
  };
}

/** POST /api/qb-budget — the Sync button. */
export async function simulatedBudgetSync(method: BudgetMethod = 'accrual') {
  const status = await simulatedBudget(method);
  return {
    ok: true,
    simulated: true,
    asOf: status.snapshot.payload.asOf,
    demo_note: 'Sync is a no-op in the demo; budget vs actuals are regenerated from the snapshot and budget-2026.json.',
  };
}
